import { toast } from "react-toastify";
import InlineAlert, { type InlineAlertProps } from "@scripts/react/components/InlineAlert";
import Button, { HoverDeleteButton } from "@scripts/react/components/Button";

type ToastAction = { label: string; onClick: () => void; className?: string };

function alertLevelToButtonType(level: InlineAlertProps["level"]) {
    if (level === "purbo") return "primary";
    if (level === "none") return "secondary";
    return level;
}

type CustomToastOptions = Partial<Pick<InlineAlertProps, "type">> &
    Omit<InlineAlertProps, "type" | "cta"> & {
        autoClose?: number | false;
        onClose?: () => void;
        toastId?: string;
        actions?: ToastAction[];
    };

function showToast(options: CustomToastOptions) {
    const { autoClose, onClose, toastId, actions, ...alertProps } = options;

    return toast(
        ({ closeToast }) => {
            const handleClose: React.MouseEventHandler<HTMLButtonElement> = (e) => {
                closeToast(e);
                onClose?.();
            };

            return (
                <InlineAlert
                    {...alertProps}
                    className="group relative"
                    cta={
                        <div className="flex items-center gap-2">
                            {actions?.map((action, i) => (
                                <Button
                                    key={i}
                                    type="secondary"
                                    size="small"
                                    onClick={action.onClick}
                                    className={action.className}
                                >
                                    {action.label}
                                </Button>
                            ))}
                            <HoverDeleteButton
                                onClick={handleClose}
                                type={alertLevelToButtonType(alertProps.level)}
                                className="-right-1.5 -top-1.5"
                            />
                        </div>
                    }
                />
            );
        },
        {
            toastId,
            autoClose,
            onClose,
            className: "w-full max-w-[90vw]",
            closeButton: false,
            style: { background: "transparent", boxShadow: "none", padding: 0 }
        }
    );
}

export function useToast() {
    return {
        success: (message: string) => showToast({ level: "success", children: message }),
        error: (message: string) => showToast({ level: "danger", children: message }),
        info: (message: string) => showToast({ level: "info", children: message }),
        warning: (message: string) => showToast({ level: "warning", children: message }),
        dismiss: (toastId?: string) => toast.dismiss(toastId),
        custom: showToast
    };
}
